import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { toast } from 'react-toastify';
import BillingNav from './BillingNav';
import { BillingPage } from './billingUi';
import {
  fetchBillingConfiguration,
  fetchBillingNotifications,
  fetchNotificationSettings,
  fetchProductMaster,
  updateProductComponent,
  fetchTaxRules,
  fetchIntegration,
  fetchBillingPermissions,
  runBillingNotifications,
  saveNotificationSettings,
  saveTaxRule,
  saveIntegration,
  setBillingPermission,
} from '../../services/billingApi';
import { fetchLookups } from '../../services/licensingApi';

const PRODUCT_PRICING_TYPES = [
  { code: 'SUBSCRIPTION', label: 'Subscription (Monthly / Yearly)' },
  { code: 'USAGE_BASED', label: 'Per-unit / usage (MB, GB, Hours…)' },
  { code: 'FIXED_AMOUNT', label: 'Fixed amount' },
];

const USAGE_UNITS = ['GB', 'MB', 'Hours', 'TOKEN', 'CALLS', 'FILE', 'CONNECTOR', 'FLOW', 'AGENT', 'USER'];

const FALLBACK_COUNTRIES = [
  { code: 'India', label: 'India' },
  { code: 'United States', label: 'United States' },
  { code: 'United Arab Emirates', label: 'United Arab Emirates' },
  { code: 'United Kingdom', label: 'United Kingdom' },
  { code: 'Singapore', label: 'Singapore' },
];

const FALLBACK_REGIONS = [
  { code: 'India::Kerala', label: 'Kerala', country: 'India' },
  { code: 'India::Maharashtra', label: 'Maharashtra', country: 'India' },
  { code: 'India::Karnataka', label: 'Karnataka', country: 'India' },
  { code: 'India::Tamil Nadu', label: 'Tamil Nadu', country: 'India' },
  { code: 'India::Delhi', label: 'Delhi', country: 'India' },
  { code: 'United States::California', label: 'California', country: 'United States' },
  { code: 'United States::New York', label: 'New York', country: 'United States' },
  { code: 'United States::Texas', label: 'Texas', country: 'United States' },
  { code: 'United Arab Emirates::Dubai', label: 'Dubai', country: 'United Arab Emirates' },
  { code: 'United Arab Emirates::Abu Dhabi', label: 'Abu Dhabi', country: 'United Arab Emirates' },
  { code: 'United Kingdom::England', label: 'England', country: 'United Kingdom' },
  { code: 'United Kingdom::Scotland', label: 'Scotland', country: 'United Kingdom' },
  { code: 'Singapore::Singapore', label: 'Singapore', country: 'Singapore' },
];

function parseRegionLookup(row) {
  const code = String(row.code || '');
  const label = String(row.label || code);
  const sep = code.indexOf('::');
  const country = sep > 0 ? code.slice(0, sep) : '';
  const regionName = sep > 0 ? code.slice(sep + 2) : label;
  return { code, label: label || regionName, country, regionName: regionName || label };
}

/** Billing Configuration: Product Master + lookup reference */
const BillingConfig = () => {
  const [data, setData] = useState(null);
  const [productMaster, setProductMaster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({
    paymentDueLeadDays: 7,
    renewalLeadDays: 30,
    paymentConfirmationEnabled: true,
    usageNotifyBillingContact: true,
    usageNotifyTenantAdmin: false,
    cdxBillingEmail: '',
  });
  const [notices, setNotices] = useState([]);
  const [busy, setBusy] = useState(false);
  const [taxRules, setTaxRules] = useState([]);
  const [taxForm, setTaxForm] = useState({ ruleCode: '', ruleName: '', country: '', region: '', ratePercent: '0' });
  const [erpUrl, setErpUrl] = useState('');
  const [payUrl, setPayUrl] = useState('');
  const [payName, setPayName] = useState('');
  const [paySecret, setPaySecret] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [savingComponentId, setSavingComponentId] = useState(null);
  const [countries, setCountries] = useState(FALLBACK_COUNTRIES);
  const [regions, setRegions] = useState(FALLBACK_REGIONS);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cfg, pm, ns, notes, rules, integ, perms, luk] = await Promise.all([
        fetchBillingConfiguration(),
        fetchProductMaster(),
        fetchNotificationSettings(),
        fetchBillingNotifications(40),
        fetchTaxRules(),
        fetchIntegration(),
        fetchBillingPermissions(),
        fetchLookups({ grouped: true }).catch(() => ({})),
      ]);
      setData(cfg);
      setProductMaster(pm);
      const countryRows = Array.isArray(luk?.BILLING_COUNTRY) ? luk.BILLING_COUNTRY : [];
      const regionRows = Array.isArray(luk?.BILLING_REGION) ? luk.BILLING_REGION : [];
      if (countryRows.length) {
        setCountries(countryRows.map((r) => ({ code: r.code, label: r.label || r.code })));
      }
      if (regionRows.length) {
        setRegions(regionRows.map(parseRegionLookup));
      }
      setSettings({
        paymentDueLeadDays: ns.paymentDueLeadDays ?? 7,
        renewalLeadDays: ns.renewalLeadDays ?? 30,
        paymentConfirmationEnabled: ns.paymentConfirmationEnabled !== false,
        usageNotifyBillingContact: ns.usageNotifyBillingContact !== false,
        usageNotifyTenantAdmin: !!ns.usageNotifyTenantAdmin,
        cdxBillingEmail: ns.cdxBillingEmail || '',
      });
      setNotices(notes);
      setTaxRules(rules);
      setErpUrl(integ.erpExportUrl || '');
      setPayUrl(integ.paymentChargeUrl || '');
      setPayName(integ.paymentProviderName || '');
      setPermissions(perms);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const regionsForCountry = regions.filter((r) => r.country === taxForm.country);

  const sections = [
    ['PAYMENT_TERMS', 'Payment terms'],
    ['BILLING_FREQUENCY', 'Billing frequency'],
    ['CHARGE_TYPE', 'Charge types'],
    ['CURRENCY', 'Currency'],
    ['INVOICE_STATUS', 'Invoice status'],
    ['PAYMENT_STATUS', 'Payment status'],
    ['PAYMENT_METHOD', 'Payment method'],
    ['USAGE_BILLING_STATUS', 'Usage billing status'],
    ['RENEWAL_STATUS', 'Renewal status'],
    ['ADJUSTMENT_TYPE', 'Adjustment type'],
    ['PRODUCT', 'Product'],
  ];

  return (
    <BillingPage>
      <BillingNav title="Billing Configuration" />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Tax rules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Country and Region / State are required dropdowns (e.g. India → Kerala) so the correct
            regional rate applies. When an account matches that country/region (or a tax rule code),
            that rate is used; otherwise the account tax percent applies.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'flex-start' }}>
            <TextField size="small" label="Code" value={taxForm.ruleCode} onChange={(e) => setTaxForm((f) => ({ ...f, ruleCode: e.target.value }))} />
            <TextField size="small" label="Name" value={taxForm.ruleName} onChange={(e) => setTaxForm((f) => ({ ...f, ruleName: e.target.value }))} />
            <FormControl size="small" sx={{ minWidth: 180 }} required>
              <InputLabel>Country</InputLabel>
              <Select
                label="Country"
                value={taxForm.country || ''}
                onChange={(e) =>
                  setTaxForm((f) => ({ ...f, country: e.target.value, region: '' }))
                }
              >
                <MenuItem value="">
                  <em>Select country</em>
                </MenuItem>
                {countries.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }} required disabled={!taxForm.country}>
              <InputLabel>Region / State</InputLabel>
              <Select
                label="Region / State"
                value={taxForm.region || ''}
                onChange={(e) => setTaxForm((f) => ({ ...f, region: e.target.value }))}
              >
                <MenuItem value="">
                  <em>Select region</em>
                </MenuItem>
                {regionsForCountry.map((r) => (
                  <MenuItem key={r.code} value={r.regionName || r.label}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" type="number" label="Rate %" value={taxForm.ratePercent} onChange={(e) => setTaxForm((f) => ({ ...f, ratePercent: e.target.value }))} />
            <Button
              variant="contained"
              disabled={busy}
              onClick={async () => {
                if (!taxForm.country) {
                  toast.error('Select a Country');
                  return;
                }
                if (!taxForm.region) {
                  toast.error('Select a Region / State');
                  return;
                }
                if (!String(taxForm.ruleCode || '').trim()) {
                  toast.error('Code is required');
                  return;
                }
                setBusy(true);
                try {
                  await saveTaxRule(taxForm);
                  setTaxRules(await fetchTaxRules());
                  setTaxForm({ ruleCode: '', ruleName: '', country: '', region: '', ratePercent: '0' });
                  toast.success('Tax rule saved');
                } catch (e) {
                  toast.error(e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save rule
            </Button>
          </Box>
          <Table size="small" sx={{ mb: 4 }}>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Region</TableCell>
                <TableCell align="right">Rate %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {taxRules.map((r) => (
                <TableRow key={r.ruleCode}>
                  <TableCell>{r.ruleCode}</TableCell>
                  <TableCell>{r.ruleName}</TableCell>
                  <TableCell>{r.country || '—'}</TableCell>
                  <TableCell>{r.region || '—'}</TableCell>
                  <TableCell align="right">{r.ratePercent}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ mb: 1 }}>
            ERP and payment provider
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Issued invoices are posted to the ERP URL. Card and ACH payments with a provider token are charged at the charge URL. The secret stays on the server. Leave the secret blank to keep the saved one.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 4, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="ERP export URL"
              value={erpUrl}
              onChange={(e) => setErpUrl(e.target.value)}
              sx={{ minWidth: 360 }}
            />
            <TextField
              size="small"
              label="Payment provider"
              value={payName}
              onChange={(e) => setPayName(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Card or bank charge URL"
              value={payUrl}
              onChange={(e) => setPayUrl(e.target.value)}
              sx={{ minWidth: 320 }}
            />
            <TextField
              size="small"
              type="password"
              label="Provider secret"
              value={paySecret}
              onChange={(e) => setPaySecret(e.target.value)}
              sx={{ minWidth: 220 }}
            />
            <Button
              variant="outlined"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const saved = await saveIntegration({
                    erpExportUrl: erpUrl,
                    paymentChargeUrl: payUrl,
                    paymentProviderName: payName,
                    paymentProviderSecret: paySecret,
                  });
                  setErpUrl(saved.erpExportUrl || '');
                  setPayUrl(saved.paymentChargeUrl || '');
                  setPayName(saved.paymentProviderName || '');
                  setPaySecret('');
                  toast.success('Integration saved');
                } catch (e) {
                  toast.error(e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save URL
            </Button>
          </Box>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Billing permissions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            CDX users can change billing. Each action below can be turned off.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', mb: 4 }}>
            {permissions.map((p) => (
              <FormControlLabel
                key={p.permissionCode}
                control={
                  <Switch
                    checked={!!p.enabled}
                    onChange={async (e) => {
                      const enabled = e.target.checked;
                      try {
                        await setBillingPermission(p.permissionCode, enabled);
                        setPermissions((rows) =>
                          rows.map((row) =>
                            row.permissionCode === p.permissionCode ? { ...row, enabled } : row
                          )
                        );
                      } catch (err) {
                        toast.error(err.message);
                      }
                    }}
                  />
                }
                label={p.permissionCode}
              />
            ))}
          </Box>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Invoice issued and payment received logs are written when those actions happen.
            Invoice email to the client is only when you click Send on Invoices.
            Due, overdue, usage 80/90/100, renewal, and grace or suspension are created when you run the scan.
            Each event is stored once. Mail is sent when SMTP is configured; otherwise the row stays logged.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              type="number"
              label="Payment due lead (days)"
              value={settings.paymentDueLeadDays}
              onChange={(e) => setSettings((s) => ({ ...s, paymentDueLeadDays: e.target.value }))}
              sx={{ width: 200 }}
            />
            <TextField
              size="small"
              type="number"
              label="Renewal lead (days)"
              value={settings.renewalLeadDays}
              onChange={(e) => setSettings((s) => ({ ...s, renewalLeadDays: e.target.value }))}
              sx={{ width: 180 }}
            />
            <TextField
              size="small"
              label="CDX billing email"
              value={settings.cdxBillingEmail}
              onChange={(e) => setSettings((s) => ({ ...s, cdxBillingEmail: e.target.value }))}
              sx={{ minWidth: 260 }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!settings.paymentConfirmationEnabled}
                  onChange={(e) => setSettings((s) => ({ ...s, paymentConfirmationEnabled: e.target.checked }))}
                />
              }
              label="Payment confirmation"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!settings.usageNotifyBillingContact}
                  onChange={(e) => setSettings((s) => ({ ...s, usageNotifyBillingContact: e.target.checked }))}
                />
              }
              label="Usage alerts to billing contact"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!settings.usageNotifyTenantAdmin}
                  onChange={(e) => setSettings((s) => ({ ...s, usageNotifyTenantAdmin: e.target.checked }))}
                />
              }
              label="Usage alerts to tenant email"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="contained"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await saveNotificationSettings(settings);
                  toast.success('Notification settings saved');
                } catch (e) {
                  toast.error(e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Save settings
            </Button>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const result = await runBillingNotifications();
                  toast.success(`Scan created ${result.created ?? 0} notification(s)`);
                  setNotices(await fetchBillingNotifications(40));
                } catch (e) {
                  toast.error(e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Run scan
            </Button>
          </Box>
          <Table size="small" sx={{ mb: 4 }}>
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>No notifications yet.</TableCell>
                </TableRow>
              ) : (
                notices.map((n) => (
                  <TableRow key={n.notificationId}>
                    <TableCell>{n.createdDate ? String(n.createdDate).replace('T', ' ').slice(0, 16) : '—'}</TableCell>
                    <TableCell>{n.eventType}</TableCell>
                    <TableCell>{n.tenantName || n.tenantId || '—'}</TableCell>
                    <TableCell>{n.recipient}</TableCell>
                    <TableCell>{n.deliveryStatus}</TableCell>
                    <TableCell>{n.message}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Product Master (main + sub-products)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Every sub-product must have a <strong>Product type</strong>: Subscription, Per-unit / usage,
            or Fixed amount. Per-unit requires a <strong>Unit</strong> (GB, MB, Hours, …). Prices are
            set under Pricing &amp; Plans after Sync from Product Master.
          </Typography>
          {productMaster.map((p) => (
            <Box key={p.productCode} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {p.productName} ({p.productCode})
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Sub-product</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Product type</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Unit</TableCell>
                    <TableCell>Charge code</TableCell>
                    <TableCell width={90} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(p.components || []).map((c) => {
                    const type = c.productPricingType || 'SUBSCRIPTION';
                    const needsUnit = type === 'USAGE_BASED';
                    return (
                      <TableRow key={c.componentId || c.componentCode}>
                        <TableCell>
                          <Typography variant="body2">{c.componentName}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.componentCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={type}
                              onChange={(e) => {
                                const next = e.target.value;
                                setProductMaster((prev) =>
                                  prev.map((prod) =>
                                    prod.productCode !== p.productCode
                                      ? prod
                                      : {
                                          ...prod,
                                          components: (prod.components || []).map((comp) =>
                                            comp.componentId === c.componentId
                                              ? {
                                                  ...comp,
                                                  productPricingType: next,
                                                  unit:
                                                    next === 'USAGE_BASED'
                                                      ? comp.unit || 'GB'
                                                      : next === 'SUBSCRIPTION' &&
                                                          String(comp.unit || '').toUpperCase() ===
                                                            'SUBSCRIPTION'
                                                        ? ''
                                                        : comp.unit,
                                                }
                                              : comp
                                          ),
                                        }
                                  )
                                );
                              }}
                            >
                              {PRODUCT_PRICING_TYPES.map((t) => (
                                <MenuItem key={t.code} value={t.code}>
                                  {t.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {needsUnit ? (
                            <FormControl fullWidth size="small">
                              <Select
                                displayEmpty
                                value={c.unit || ''}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setProductMaster((prev) =>
                                    prev.map((prod) =>
                                      prod.productCode !== p.productCode
                                        ? prod
                                        : {
                                            ...prod,
                                            components: (prod.components || []).map((comp) =>
                                              comp.componentId === c.componentId
                                                ? { ...comp, unit: next }
                                                : comp
                                            ),
                                          }
                                    )
                                  );
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select unit</em>
                                </MenuItem>
                                {USAGE_UNITS.map((u) => (
                                  <MenuItem key={u} value={u}>
                                    {u}
                                  </MenuItem>
                                ))}
                                {c.unit && !USAGE_UNITS.includes(c.unit) ? (
                                  <MenuItem value={c.unit}>{c.unit}</MenuItem>
                                ) : null}
                              </Select>
                            </FormControl>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{c.chargeCode || '—'}</TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            startIcon={<Save fontSize="small" />}
                            disabled={savingComponentId === c.componentId || !c.componentId}
                            onClick={async () => {
                              if (!c.componentId) return;
                              if (needsUnit && !c.unit) {
                                toast.error('Per-unit type requires a Unit (GB, MB, Hours…)');
                                return;
                              }
                              setSavingComponentId(c.componentId);
                              try {
                                const saved = await updateProductComponent(c.componentId, {
                                  productPricingType: type,
                                  unit: needsUnit ? c.unit : c.unit || null,
                                });
                                setProductMaster((prev) =>
                                  prev.map((prod) =>
                                    prod.productCode !== p.productCode
                                      ? prod
                                      : {
                                          ...prod,
                                          components: (prod.components || []).map((comp) =>
                                            comp.componentId === c.componentId
                                              ? { ...comp, ...saved }
                                              : comp
                                          ),
                                        }
                                  )
                                );
                                toast.success(`Saved ${c.componentName}`);
                              } catch (e) {
                                toast.error(e.message || 'Save failed');
                              } finally {
                                setSavingComponentId(null);
                              }
                            }}
                          >
                            Save
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          ))}

          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {PRODUCT_PRICING_TYPES.map((t) => (
              <Chip key={t.code} size="small" label={t.label} variant="outlined" />
            ))}
          </Box>

          <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
            Lookups
          </Typography>
          {sections.map(([key, label]) => (
            <Box key={key} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {label}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Label</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.[key] || []).map((r) => (
                    <TableRow key={r.code}>
                      <TableCell>{r.code}</TableCell>
                      <TableCell>{r.label}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
        </>
      )}
    </BillingPage>
  );
};

export default BillingConfig;
